import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { Plus, Building2, Pencil, Trash2, FileSpreadsheet, Download } from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Modal } from '../../../components/ui/Modal'
import api from '../../../lib/api'

interface SubcontractorCompany {
  id: string
  name: string
  vatNumber?: string
  address?: string
  city?: string
  email?: string
  phone?: string
  companyType: string
  parentCompanyId: string
}

interface ImportResult {
  mastersFound?: number
  subcontractorsCreated?: number
  sitesCreated?: number
  skipped?: number
}

const subcontractorSchema = z.object({
  name: z.string().min(1, 'Ragione sociale obbligatoria'),
  address: z.string().optional(),
  city: z.string().optional(),
  postalCode: z.string().optional(),
  vatNumber: z.string().optional(),
  fiscalCode: z.string().optional(),
  email: z.string().email('Email non valida').optional().or(z.literal('')),
  phone: z.string().optional(),
  contactName: z.string().optional(),
  contactPhone: z.string().optional(),
  contactEmail: z.string().email('Email referente non valida').optional().or(z.literal('')),
})

type SubcontractorFormData = z.infer<typeof subcontractorSchema>

interface SubcontractorFormModalProps {
  parentCompanyId: string
  subcontractor?: SubcontractorCompany | null
  open: boolean
  onClose: () => void
}

function SubcontractorFormModal({ parentCompanyId, subcontractor, open, onClose }: SubcontractorFormModalProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(subcontractor?.id)

  const { register, handleSubmit, formState: { errors }, reset } = useForm<SubcontractorFormData>({
    resolver: zodResolver(subcontractorSchema),
    defaultValues: {
      name: subcontractor?.name ?? '',
      address: subcontractor?.address ?? '',
      city: subcontractor?.city ?? '',
      postalCode: '',
      vatNumber: subcontractor?.vatNumber ?? '',
      fiscalCode: '',
      email: subcontractor?.email ?? '',
      phone: subcontractor?.phone ?? '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: SubcontractorFormData) => {
      if (isEdit && subcontractor?.id) {
        await api.patch(`/companies/${subcontractor.id}`, {
          name: data.name,
          vatNumber: data.vatNumber,
          address: data.address,
          city: data.city,
          email: data.email,
          phone: data.phone,
        })
      } else {
        await api.post(`/companies/${parentCompanyId}/subcontractors`, data)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subcontractors', parentCompanyId] })
      reset()
      onClose()
    },
  })

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose() }}
      title={isEdit ? 'Modifica subappaltato' : 'Nuovo subappaltato'}
      size="lg"
    >
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-3">
        <Input
          label="Ragione sociale *"
          {...register('name')}
          error={errors.name?.message}
          placeholder="Azienda Sub S.r.l."
        />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Indirizzo sede" {...register('address')} placeholder="Via Roma 1" />
          <Input label="Città" {...register('city')} placeholder="Milano" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="CAP" {...register('postalCode')} placeholder="20100" />
          <Input label="P.IVA (opzionale)" {...register('vatNumber')} placeholder="IT12345678901" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input label="Codice Fiscale" {...register('fiscalCode')} placeholder="12345678901" />
          <Input
            label="Email"
            type="email"
            {...register('email')}
            error={errors.email?.message}
            placeholder="info@azienda.it"
          />
        </div>

        <Input label="Telefono" {...register('phone')} placeholder="+39 02 000000" />

        {!isEdit && (
          <>
            <div className="border-t border-[var(--fc-border)] pt-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[var(--fc-foreground-muted)] mb-3">
                Referente sede
              </p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Referente — Nome" {...register('contactName')} placeholder="Mario Rossi" />
                  <Input label="Referente — Telefono" {...register('contactPhone')} placeholder="+39 348 0000000" />
                </div>
                <Input
                  label="Referente — Email"
                  type="email"
                  {...register('contactEmail')}
                  error={errors.contactEmail?.message}
                  placeholder="m.rossi@azienda.it"
                />
              </div>
            </div>
          </>
        )}

        {mutation.isError && (
          <p className="text-sm text-[var(--fc-danger)]">Errore durante il salvataggio.</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={() => { reset(); onClose() }}>
            Annulla
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Salvataggio...' : isEdit ? 'Salva' : 'Aggiungi'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export interface SubcontractorListProps {
  parentCompanyId: string
  parentCompanyName: string
}

export function SubcontractorsList({ parentCompanyId, parentCompanyName: _parentCompanyName }: SubcontractorListProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editingSubcontractor, setEditingSubcontractor] = useState<SubcontractorCompany | null>(null)
  const [showImport, setShowImport] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [previewRows, setPreviewRows] = useState<{ masterName: string; subName: string; address: string }[] | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)

  const { data: subcontractors = [], isLoading } = useQuery({
    queryKey: ['subcontractors', parentCompanyId],
    queryFn: async () => {
      try {
        const res = await api.get<SubcontractorCompany[]>(`/companies/${parentCompanyId}/subcontractors`)
        return res.data ?? []
      } catch {
        return [] as SubcontractorCompany[]
      }
    },
    enabled: Boolean(parentCompanyId),
  })

  const deleteMutation = useMutation({
    mutationFn: async (subId: string) => {
      await api.delete(`/companies/${subId}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subcontractors', parentCompanyId] })
    },
  })

  const previewMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post<{ rows: { masterName: string; subName: string; address: string }[] }>(
        '/companies/import/subcontractors/preview', formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      return res.data
    },
    onSuccess: (data) => setPreviewRows(data.rows),
  })

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post<ImportResult>('/companies/import/subcontractors', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: (data) => {
      setImportResult(data)
      setPreviewRows(null)
      setPendingFile(null)
      void queryClient.invalidateQueries({ queryKey: ['subcontractors', parentCompanyId] })
      if (fileInputRef.current) fileInputRef.current.value = ''
    },
  })

  const openAdd = () => {
    setEditingSubcontractor(null)
    setModalOpen(true)
  }

  const openEdit = (sub: SubcontractorCompany) => {
    setEditingSubcontractor(sub)
    setModalOpen(true)
  }

  const handleDelete = (sub: SubcontractorCompany) => {
    if (window.confirm(`Eliminare il subappaltato "${sub.name}"?`)) {
      deleteMutation.mutate(sub.id)
    }
  }

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImportResult(null)
      setPreviewRows(null)
      setPendingFile(file)
      previewMutation.mutate(file)
    }
  }

  const handleConfirmImport = () => {
    if (pendingFile) importMutation.mutate(pendingFile)
  }

  const handleCancelImport = () => {
    setPreviewRows(null)
    setPendingFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/companies/import/subcontractors/template', { responseType: 'blob' })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'template_subappaltati.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      // silently ignore
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-sm text-[var(--fc-foreground-muted)]">
        Caricamento subappaltati...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm text-[var(--fc-foreground-muted)]">
          {subcontractors.length} {subcontractors.length === 1 ? 'subappaltato' : 'subappaltati'}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={handleDownloadTemplate}
          >
            Scarica template
          </Button>
          <Button
            size="sm"
            variant="secondary"
            leftIcon={<FileSpreadsheet className="h-4 w-4" />}
            onClick={() => { setShowImport((v) => !v); setImportResult(null) }}
          >
            Importa da Excel
          </Button>
          <Button
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={openAdd}
          >
            Aggiungi subappaltato
          </Button>
        </div>
      </div>

      {showImport && (
        <div className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 space-y-3">
          <p className="text-sm font-medium text-[var(--fc-foreground)]">
            Carica il file Excel (3 colonne: Cliente Master, Subappaltato, Indirizzo sede)
          </p>

          {!previewRows && !importResult && (
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFile}
                className="text-sm text-[var(--fc-foreground)] file:mr-3 file:h-8 file:cursor-pointer file:rounded file:border-0 file:bg-[var(--fc-surface-overlay)] file:px-3 file:text-sm file:font-medium file:text-[var(--fc-foreground)] hover:file:bg-[var(--fc-gray-200)]"
              />
              {previewMutation.isPending && (
                <span className="text-sm text-[var(--fc-foreground-muted)]">Lettura file...</span>
              )}
            </div>
          )}

          {previewMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore nella lettura del file.</p>
          )}

          {previewRows && previewRows.length > 0 && !importResult && (
            <div className="space-y-3">
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                Anteprima — <strong>{previewRows.length} righe</strong> trovate nel file. Verifica i dati e conferma l&apos;importazione.
              </p>
              <div className="max-h-64 overflow-y-auto rounded border border-[var(--fc-border)]">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-[var(--fc-surface-overlay)]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Cliente Master</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Subappaltato</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Indirizzo sede</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className={`border-t border-[var(--fc-border)] ${i % 2 === 0 ? '' : 'bg-[var(--fc-surface-overlay)]'}`}>
                        <td className="px-3 py-1.5 font-medium text-[var(--fc-foreground)]">{row.masterName}</td>
                        <td className="px-3 py-1.5 text-[var(--fc-foreground)]">{row.subName}</td>
                        <td className="px-3 py-1.5 text-[var(--fc-foreground-muted)]">{row.address || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleConfirmImport}
                  disabled={importMutation.isPending}
                >
                  {importMutation.isPending ? 'Importazione in corso...' : `Importa ${previewRows.length} righe`}
                </Button>
                <Button size="sm" variant="secondary" onClick={handleCancelImport}>
                  Annulla
                </Button>
              </div>
            </div>
          )}

          {previewRows && previewRows.length === 0 && !importResult && (
            <p className="text-sm text-[var(--fc-foreground-muted)]">Il file non contiene righe valide.</p>
          )}

          {importMutation.isError && (
            <p className="text-sm text-[var(--fc-danger)]">Errore durante l&apos;importazione.</p>
          )}

          {importResult && (
            <div className="rounded border border-[var(--fc-border)] bg-[var(--fc-surface-overlay)] px-3 py-2 text-sm space-y-1">
              <p className="font-medium text-[var(--fc-success)]">Importazione completata</p>
              <ul className="text-[var(--fc-foreground-muted)] space-y-0.5">
                {importResult.mastersFound !== undefined && <li>Clienti trovati/creati: {importResult.mastersFound}</li>}
                {importResult.subcontractorsCreated !== undefined && <li>Subappaltati creati: {importResult.subcontractorsCreated}</li>}
                {importResult.sitesCreated !== undefined && <li>Sedi create: {importResult.sitesCreated}</li>}
                {importResult.skipped !== undefined && <li>Righe saltate: {importResult.skipped}</li>}
              </ul>
              <Button size="sm" variant="secondary" className="mt-2" onClick={() => { setImportResult(null); if (fileInputRef.current) fileInputRef.current.value = '' }}>
                Importa altro file
              </Button>
            </div>
          )}
        </div>
      )}

      {subcontractors.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <Building2 className="h-10 w-10 text-[var(--fc-foreground-muted)] mb-2" aria-hidden="true" />
          <p className="text-sm font-medium text-[var(--fc-foreground)]">Nessun subappaltato</p>
          <p className="text-xs text-[var(--fc-foreground-muted)] mt-0.5">
            Aggiungi il primo cliente in subappalto
          </p>
          <Button size="sm" variant="secondary" className="mt-3" onClick={openAdd}>
            Aggiungi subappaltato
          </Button>
        </div>
      ) : (
        <ul className="space-y-2">
          {subcontractors.map((sub) => (
            <li
              key={sub.id}
              className="rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] overflow-hidden"
            >
              <div className="flex items-start justify-between p-3">
                <button
                  type="button"
                  className="text-left min-w-0 flex-1 space-y-0.5 cursor-pointer"
                  onClick={() => navigate(`/companies/${sub.id}`)}
                  aria-label={`Apri pagina ${sub.name}`}
                >
                  <p className="font-medium text-[var(--fc-foreground)] hover:text-[var(--fc-primary)] transition-colors">
                    {sub.name}
                  </p>
                  {sub.vatNumber && (
                    <p className="text-xs text-[var(--fc-foreground-muted)]">P.IVA {sub.vatNumber}</p>
                  )}
                  {(sub.address || sub.city) && (
                    <p className="text-xs text-[var(--fc-foreground-muted)] truncate">
                      {[sub.address, sub.city].filter(Boolean).join(', ')}
                    </p>
                  )}
                </button>

                <div className="flex gap-1 shrink-0 ml-2">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => openEdit(sub)}
                    aria-label={`Modifica ${sub.name}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    onClick={() => handleDelete(sub)}
                    aria-label={`Elimina ${sub.name}`}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-[var(--fc-danger)]" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <SubcontractorFormModal
        parentCompanyId={parentCompanyId}
        subcontractor={editingSubcontractor}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  )
}
